import type { JsonRpcResult } from '@json-rpc-tools/utils'
import { formatJsonRpcResult } from '@json-rpc-tools/utils'
import type { AccountId } from '@shapeshiftoss/caip'
import { fromAccountId } from '@shapeshiftoss/caip'
import type { EvmChainAdapter } from '@shapeshiftoss/chain-adapters'
import { toAddressNList } from '@shapeshiftoss/chain-adapters'
import type { ETHSignedTypedData, HDWallet } from '@shapeshiftoss/hdwallet-core'
import type { KeepKeyHDWallet } from '@shapeshiftoss/hdwallet-keepkey'
import type { NativeHDWallet } from '@shapeshiftoss/hdwallet-native'
import { isTrezor } from '@shapeshiftoss/hdwallet-trezor'
import type { AccountMetadata } from '@shapeshiftoss/types'
import { getSdkError } from '@walletconnect/utils'

import { assertIsDefined } from '@/lib/utils'
import type {
  CustomTransactionData,
  SupportedSessionRequest,
} from '@/plugins/walletConnectToDapps/types'
import { EIP155_SigningMethod } from '@/plugins/walletConnectToDapps/types'
import {
  convertNumberToHex,
  getFeesForTx,
  getGasData,
  getSignParamsMessage,
} from '@/plugins/walletConnectToDapps/utils'

type ApproveEIP155RequestArgs = {
  requestEvent: SupportedSessionRequest
  wallet: HDWallet
  chainAdapter: EvmChainAdapter
  accountMetadata?: AccountMetadata
  customTransactionData?: CustomTransactionData
  accountId?: AccountId
}

function assertSupportsEthSignTypedData(
  wallet: HDWallet,
): asserts wallet is KeepKeyHDWallet | NativeHDWallet {
  if (!(wallet as KeepKeyHDWallet | NativeHDWallet).ethSignTypedData)
    throw new Error('approveEIP155Request: ethSignTypedData not supported')
}

export const approveEIP155Request = async ({
  requestEvent,
  wallet,
  chainAdapter,
  accountMetadata,
  customTransactionData,
  accountId,
}: ApproveEIP155RequestArgs): Promise<JsonRpcResult<ETHSignedTypedData | string>> => {
  const { params, id } = requestEvent
  const { request } = params
  const bip44Params = accountMetadata?.bip44Params
  const accountNumber = bip44Params?.accountNumber
  const addressNList = bip44Params ? toAddressNList(chainAdapter.getBip44Params(bip44Params)) : []

  switch (request.method) {
    case EIP155_SigningMethod.PERSONAL_SIGN:
    case EIP155_SigningMethod.ETH_SIGN: {
      const message = getSignParamsMessage(request.params, false)
      const messageToSign = { addressNList, message }
      const input = { messageToSign, wallet }
      const signedMessage = await chainAdapter.signMessage(input)
      if (!signedMessage) throw new Error('approveEIP155Request: signMessage failed')
      return formatJsonRpcResult(id, signedMessage)
    }

    case EIP155_SigningMethod.ETH_SIGN_TYPED_DATA:
    case EIP155_SigningMethod.ETH_SIGN_TYPED_DATA_V3:
    case EIP155_SigningMethod.ETH_SIGN_TYPED_DATA_V4: {
      assertSupportsEthSignTypedData(wallet)

      const payloadString = request.params[1]
      const typedData = JSON.parse(payloadString)
      const messageToSign = { addressNList, typedData }
      const signedData = await wallet.ethSignTypedData(messageToSign)
      if (!signedData) throw new Error('approveEIP155Request: signMessage failed')
      return formatJsonRpcResult(id, signedData.signature)
    }

    case EIP155_SigningMethod.ETH_SEND_TRANSACTION: {
      assertIsDefined(customTransactionData)
      assertIsDefined(accountNumber)
      assertIsDefined(accountId)

      const sendTransaction = request.params[0]
      const maybeAdvancedParamsNonce = customTransactionData.nonce
        ? convertNumberToHex(customTransactionData.nonce)
        : null
      const fees = await getFeesForTx(sendTransaction, chainAdapter, accountId)
      const senderAddress = await chainAdapter.getAddress({
        accountNumber,
        wallet,
        pubKey: isTrezor(wallet) && accountId ? fromAccountId(accountId).account : undefined,
      })
      const gasData = getGasData(customTransactionData, fees)
      const gasLimit = (() => {
        if (customTransactionData.gasLimit) return customTransactionData.gasLimit
        if (sendTransaction.gasLimit) return sendTransaction.gasLimit
      })()

      // This shouldn't happen but it may as far as types are concerned - if we have neither of Tenderly-simulated gas limit (we should)
      // nor Tx-enforced gas limit (not all provide it) then something is most definitely wrong, no point in trying with a super high gas limit e.g 90000

      if (!gasLimit) throw new Error('approveEIP155Request: missing gasLimit')

      const { txToSign: txToSignWithPossibleWrongNonce } = await chainAdapter.buildCustomTx({
        wallet,
        accountNumber,
        to: sendTransaction.to,
        data: sendTransaction.data,
        value: sendTransaction.value ?? '0',
        // https://docs.walletconnect.com/2.0/advanced/rpc-reference/ethereum-rpc#eth_sendtransaction
        gasLimit,
        ...gasData,
        pubKey: isTrezor(wallet) && accountId ? fromAccountId(accountId).account : undefined,
      })

      // Determine which nonce to use:
      // 1. If dApp provided a nonce and user's form nonce differs from it, user manually changed it
      // 2. If dApp didn't provide a nonce, check if user's nonce differs from what chain adapter fetched
      // 3. Otherwise use the fresh nonce from buildCustomTx
      const didUserManuallyChangeNonce = (() => {
        if (!maybeAdvancedParamsNonce) return false

        // If dApp provided a nonce, check if user changed it from that
        if (sendTransaction.nonce) {
          return maybeAdvancedParamsNonce !== sendTransaction.nonce
        }

        // If no dApp nonce, check if user's nonce differs from the fresh chain nonce
        // This prevents using stale cached values
        return maybeAdvancedParamsNonce !== txToSignWithPossibleWrongNonce.nonce
      })()

      const txToSign = {
        ...txToSignWithPossibleWrongNonce,
        nonce:
          didUserManuallyChangeNonce && maybeAdvancedParamsNonce
            ? maybeAdvancedParamsNonce
            : txToSignWithPossibleWrongNonce.nonce,
      }
      const signedTx = await chainAdapter.signTransaction({
        txToSign,
        wallet,
      })
      const txHash = await chainAdapter.broadcastTransaction({
        senderAddress,
        receiverAddress: txToSign.to,
        hex: signedTx,
      })

      return formatJsonRpcResult(id, txHash)
    }

    case EIP155_SigningMethod.ETH_SIGN_TRANSACTION: {
      assertIsDefined(customTransactionData)
      assertIsDefined(accountId)

      const signTransaction = request.params[0]
      const nonce = customTransactionData.nonce
        ? convertNumberToHex(customTransactionData.nonce)
        : signTransaction.nonce
      if (!nonce) throw new Error('approveEIP155Request: missing nonce')
      const gasLimit =
        (customTransactionData.gasLimit
          ? convertNumberToHex(customTransactionData.gasLimit)
          : signTransaction.gasLimit) ?? convertNumberToHex(90000) // https://docs.walletconnect.com/2.0/advanced/rpc-reference/ethereum-rpc#eth_sendtransaction
      const fees = await getFeesForTx(signTransaction, chainAdapter, accountId)
      const gasData = getGasData(customTransactionData, fees)
      const signature = await chainAdapter.signTransaction({
        txToSign: {
          addressNList,
          chainId: parseInt(fromAccountId(accountId).chainReference),
          data: signTransaction.data,
          gasLimit,
          nonce,
          to: signTransaction.to,
          value: signTransaction.value ?? convertNumberToHex(0),
          ...gasData,
        },
        wallet,
      })
      return formatJsonRpcResult(id, signature)
    }

    default:
      throw new Error(getSdkError('INVALID_METHOD').message)
  }
}
